import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/core/firebase';
import { Button } from '../components/ui';
import { Link } from 'react-router-dom';

export default function Beta() {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email && !phone) {
      setError('Please enter your email or phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await addDoc(collection(db, 'betaSignups'), {
        email: email || null,
        phone: phone || null,
        source: 'web',
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error saving signup:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="max-w-2xl mx-auto px-4 py-16">
        {/* Logo */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-primary-400">Start Grounded</h1>
          </Link>
        </div>

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-block bg-primary-500/20 text-primary-400 text-sm font-medium px-4 py-2 rounded-full mb-6">
            iOS TestFlight Beta
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            The AI trainer that <span className="text-primary-400">actually calls you</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-xl mx-auto">
            Not another notification you'll ignore. Real phone calls when you're about to skip a workout.
          </p>
        </div>

        {/* Signup Form */}
        {!submitted ? (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-12">
            <h3 className="text-xl font-semibold mb-2 text-center">Join the Beta</h3>
            <p className="text-gray-400 text-center mb-6">
              Get early access to the iOS app. Limited spots.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Phone <span className="text-gray-500">(optional - for TestFlight invite)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm">{error}</p>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full py-4 text-lg"
                disabled={loading}
                isLoading={loading}
              >
                Get Early Access
              </Button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              We'll send you a TestFlight invite when your spot is ready. No spam.
            </p>
          </div>
        ) : (
          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 mb-12 text-center">
            <h3 className="text-2xl font-bold text-green-400 mb-2">You're on the list!</h3>
            <p className="text-gray-300">
              We'll send your TestFlight invite soon. Check your email.
            </p>
          </div>
        )}

        {/* What You Get */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold mb-6 text-center">What beta testers get:</h3>
          <div className="grid gap-4">
            {[
              { text: 'Phone call reminders 30 min before workouts' },
              { text: 'AI-generated workouts tailored to your goals' },
              { text: 'Direct line to the founder for feedback' },
              { text: 'Free premium access during beta' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl p-4">
                <span className="h-2.5 w-2.5 rounded-full bg-primary-400" />
                <span className="text-gray-200">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Social Proof */}
        <div className="text-center text-gray-400 mb-12">
          <p className="italic">"Finally, someone who notices when I don't show up."</p>
        </div>

        {/* Footer Links */}
        <div className="flex justify-center gap-6 text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-gray-300">Privacy</Link>
          <Link to="/terms" className="hover:text-gray-300">Terms</Link>
          <Link to="/" className="hover:text-gray-300">Home</Link>
        </div>
      </div>
    </div>
  );
}
